/**
 * SoundCanvas - Mobile-first responsive canvas where sound becomes visual
 * High contrast, large shapes, smooth animations
 */

import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import type { VisualMark, TextureType, Layer, PaletteTheme } from './types';

interface SoundCanvasProps {
  marks: VisualMark[];
  layers?: Layer[]; // Optional layers for multi-layer display
  isListening: boolean;
  isPlaying: boolean;
  paletteTheme: PaletteTheme;
}

// Color palettes for different themes
const ORANGE_PALETTE = {
  colors: ['#FFF3E0', '#FFD180', '#FFCCBC', '#FFAB91', '#FFF9C4', '#FFEB3B', '#FFB300', '#F57C00'],
  backgrounds: ['#0D47A1', '#0D3B66', '#004D40']
};

const BLACK_WHITE_PALETTE = {
  colors: ['#FFFFFF', '#F5F5F5', '#E0E0E0', '#CCCCCC', '#B0B0B0'],
  backgrounds: ['#000000']
};

const PATTERNS_PALETTE = {
  colors: ['#FBBF24', '#F59E0B', '#FCD34D'], // Yellow tones for patterns
  backgrounds: ['#1E3A8A', '#1E40AF', '#2563EB'], // Blue backgrounds
  baseShapeColor: '#3B82F6' // Blue for base shapes
};

export function SoundCanvas({ marks, layers, isListening, isPlaying, paletteTheme }: SoundCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match display size (responsive)
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Get current palette
    const getCurrentPalette = () => {
      switch (paletteTheme) {
        case 'blackWhite':
          return BLACK_WHITE_PALETTE;
        case 'patterns':
          return PATTERNS_PALETTE;
        case 'orange':
        default:
          return ORANGE_PALETTE;
      }
    };

    // Animation loop
    const draw = () => {
      const palette = getCurrentPalette();

      // Alternate background color every 5 seconds
      const colorIndex = Math.floor(Date.now() / 5000) % palette.backgrounds.length;
      ctx.fillStyle = palette.backgrounds[colorIndex];
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const rect = canvas.getBoundingClientRect();

      // Draw marks from all layers if available, otherwise just marks
      const allMarks = layers
        ? layers.filter(l => l.isActive).flatMap(l => l.marks)
        : marks;

      allMarks.forEach(mark => {
        const x = mark.x * rect.width;
        const y = mark.y * rect.height;
        const size = mark.size;

        ctx.save();
        ctx.globalAlpha = mark.opacity;

        // Override color based on theme
        let fillColor = mark.color;
        if (paletteTheme === 'blackWhite') {
          // Force all shapes to white in black & white theme
          fillColor = '#FFFFFF';
        } else if (paletteTheme === 'patterns') {
          // For patterns theme, use blue base color for shapes
          fillColor = PATTERNS_PALETTE.baseShapeColor;
        }

        ctx.fillStyle = fillColor;

        // Apply rotation
        ctx.translate(x, y);
        ctx.rotate(mark.rotation);

        // Draw shape with texture (centered at origin now)
        if (paletteTheme === 'patterns') {
          drawShapeWithPattern(ctx, 0, 0, size, mark.texture, mark);
        } else {
          drawShape(ctx, 0, 0, size, mark.texture);
        }

        // Add glow effect for accessibility
        ctx.shadowBlur = 20;
        ctx.shadowColor = fillColor;
        ctx.fill();

        ctx.restore();
      });

      // Listening indicator
      if (isListening) {
        drawListeningPulse(ctx, rect.width, rect.height);
      }

      // Playing indicator
      if (isPlaying) {
        drawPlayingWaves(ctx, rect.width, rect.height);
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [marks, layers, isListening, isPlaying, paletteTheme]);

  return (
    <motion.canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full touch-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    />
  );
}

// Helper: Draw shape with texture pattern
function drawShape(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  texture: TextureType
) {
  switch (texture) {
    case 'dots':
      // Circle with dotted pattern (centered at origin)
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const dx = Math.cos(angle) * size * 0.3;
        const dy = Math.sin(angle) * size * 0.3;
        ctx.beginPath();
        ctx.arc(dx, dy, size * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }
      break;

    case 'waves':
      // Wavy circle (centered at origin)
      ctx.beginPath();
      for (let i = 0; i <= 20; i++) {
        const angle = (i / 20) * Math.PI * 2;
        const wave = Math.sin(angle * 3) * size * 0.1;
        const r = size + wave;
        const px = Math.cos(angle) * r;
        const py = Math.sin(angle) * r;
        if (i === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
      }
      ctx.closePath();
      ctx.fill();
      break;

    case 'stars':
      // Star shape (centered at origin)
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const angle = (i / 10) * Math.PI * 2;
        const r = i % 2 === 0 ? size : size * 0.5;
        const px = Math.cos(angle) * r;
        const py = Math.sin(angle) * r;
        if (i === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
      }
      ctx.closePath();
      ctx.fill();
      break;

    case 'hearts':
      // Heart shape (centered at origin)
      ctx.beginPath();
      ctx.moveTo(0, size * 0.3);
      ctx.bezierCurveTo(
        0, 0,
        -size * 0.5, 0,
        -size * 0.5, size * 0.3
      );
      ctx.bezierCurveTo(
        -size * 0.5, size * 0.7,
        0, size,
        0, size
      );
      ctx.bezierCurveTo(
        0, size,
        size * 0.5, size * 0.7,
        size * 0.5, size * 0.3
      );
      ctx.bezierCurveTo(
        size * 0.5, 0,
        0, 0,
        0, size * 0.3
      );
      ctx.fill();
      break;

    case 'lines':
      // Lined circle (centered at origin)
      ctx.beginPath();
      ctx.arc(0, 0, size, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = 3;
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(
          Math.cos(angle) * size,
          Math.sin(angle) * size
        );
        ctx.stroke();
      }
      break;

    case 'watercolor':
      // Watercolor bloom - soft overlapping circles with varying opacity (centered at origin)
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        const offsetX = Math.cos(angle) * size * 0.3;
        const offsetY = Math.sin(angle) * size * 0.3;
        ctx.save();
        ctx.globalAlpha = ctx.globalAlpha * (0.3 + Math.random() * 0.4);
        ctx.beginPath();
        ctx.arc(offsetX, offsetY, size * (0.8 + Math.random() * 0.4), 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      break;

    case 'splash':
      // Paint splash - random splatters (centered at origin)
      for (let i = 0; i < 12; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * size;
        const splashSize = size * (0.1 + Math.random() * 0.2);
        ctx.save();
        ctx.globalAlpha = ctx.globalAlpha * (0.5 + Math.random() * 0.5);
        ctx.beginPath();
        ctx.arc(
          Math.cos(angle) * distance,
          Math.sin(angle) * distance,
          splashSize,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.restore();
      }
      break;

    case 'bloom':
      // Diffused color bloom - radial gradient effect (centered at origin)
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 1.5);
      const currentColor = ctx.fillStyle as string;
      gradient.addColorStop(0, currentColor);
      gradient.addColorStop(0.5, currentColor.replace(')', ', 0.6)').replace('rgb', 'rgba'));
      gradient.addColorStop(1, currentColor.replace(')', ', 0)').replace('rgb', 'rgba'));
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, size * 1.5, 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'gradient':
      // Vibrant radial gradient (centered at origin)
      const radialGrad = ctx.createRadialGradient(0, 0, size * 0.2, 0, 0, size);
      const baseColor = ctx.fillStyle as string;
      radialGrad.addColorStop(0, '#FFFFFF');
      radialGrad.addColorStop(0.3, baseColor);
      radialGrad.addColorStop(1, baseColor.replace(')', ', 0.3)').replace('rgb', 'rgba'));
      ctx.fillStyle = radialGrad;
      ctx.beginPath();
      ctx.arc(0, 0, size, 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'triangle':
      // Filled triangle (centered at origin)
      ctx.beginPath();
      ctx.moveTo(0, -size);
      ctx.lineTo(-size, size);
      ctx.lineTo(size, size);
      ctx.closePath();
      ctx.fill();
      break;

    case 'rectangle':
      // Filled rectangle (centered at origin)
      ctx.fillRect(-size, -size * 0.6, size * 2, size * 1.2);
      break;

    case 'hexagon':
      // Filled hexagon (centered at origin)
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        const px = size * Math.cos(angle);
        const py = size * Math.sin(angle);
        if (i === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
      }
      ctx.closePath();
      ctx.fill();
      break;

    case 'circles':
    default:
      // Simple filled circle (centered at origin)
      ctx.beginPath();
      ctx.arc(0, 0, size, 0, Math.PI * 2);
      ctx.fill();
      break;
  }
}

// Visual feedback: Listening pulse
function drawListeningPulse(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
) {
  const time = Date.now() / 1000;
  const pulse = Math.sin(time * 3) * 0.3 + 0.7;

  // Make radius responsive to viewport size (use smaller dimension to ensure it fits)
  const minDimension = Math.min(width, height);
  const baseRadius = minDimension * 0.15; // 15% of smallest dimension

  ctx.save();
  ctx.globalAlpha = 0.3 * pulse;
  ctx.strokeStyle = '#60A5FA'; // Blue tone
  ctx.lineWidth = Math.max(4, minDimension * 0.01); // Responsive line width
  ctx.beginPath();
  ctx.arc(width / 2, height / 2, baseRadius * pulse, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

// Visual feedback: Playing waves
function drawPlayingWaves(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
) {
  const time = Date.now() / 1000;

  for (let i = 0; i < 3; i++) {
    const offset = i * 0.5;
    const wave = Math.sin(time * 2 + offset) * 30;

    ctx.save();
    ctx.globalAlpha = 0.2;
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(0, height / 2 + wave);
    ctx.lineTo(width, height / 2 + wave);
    ctx.stroke();
    ctx.restore();
  }
}

// Draw shape with pattern overlay (for patterns theme)
function drawShapeWithPattern(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  texture: TextureType,
  mark: VisualMark
) {
  // First draw the base blue shape
  drawShape(ctx, x, y, size, texture);

  // Then overlay with yellow pattern
  ctx.save();

  // Create clipping region in shape of the base shape
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.clip();

  // Determine pattern type based on mark ID hash
  const patternType = Math.abs(mark.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % 3;

  ctx.strokeStyle = '#FBBF24'; // Yellow
  ctx.fillStyle = '#FBBF24';
  ctx.lineWidth = 2;

  if (patternType === 0) {
    // Striped pattern
    for (let i = -size * 2; i < size * 2; i += 8) {
      ctx.beginPath();
      ctx.moveTo(i, -size * 2);
      ctx.lineTo(i, size * 2);
      ctx.stroke();
    }
  } else if (patternType === 1) {
    // Dotted pattern
    for (let dx = -size; dx <= size; dx += 12) {
      for (let dy = -size; dy <= size; dy += 12) {
        if (dx * dx + dy * dy <= size * size) {
          ctx.beginPath();
          ctx.arc(dx, dy, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  } else {
    // Cross-hatched pattern
    for (let i = -size * 2; i < size * 2; i += 10) {
      // Diagonal lines /
      ctx.beginPath();
      ctx.moveTo(i, -size * 2);
      ctx.lineTo(i + size * 4, size * 2);
      ctx.stroke();
      // Diagonal lines \
      ctx.beginPath();
      ctx.moveTo(i, size * 2);
      ctx.lineTo(i + size * 4, -size * 2);
      ctx.stroke();
    }
  }

  ctx.restore();
}

// Map pitch (0-1) to color based on palette
export function pitchToColor(pitch: number, palette: PaletteTheme = 'orange'): string {
  if (palette === 'blackWhite') {
    // For black and white, map pitch to grayscale
    const colors = BLACK_WHITE_PALETTE.colors;
    const index = Math.floor(pitch * (colors.length - 1));
    return colors[index];
  } else if (palette === 'patterns') {
    // For patterns, use yellow tones
    const colors = PATTERNS_PALETTE.colors;
    const index = Math.floor(pitch * (colors.length - 1));
    return colors[index];
  } else {
    // Orange theme - original behavior
    const colors = ORANGE_PALETTE.colors;
    if (pitch < 0.2) return colors[7];   // F57C00 - Dark orange
    if (pitch < 0.4) return colors[3];   // FFAB91 - Peach
    if (pitch < 0.6) return colors[6];   // FFB300 - Golden
    if (pitch < 0.8) return colors[1];   // FFD180 - Light orange
    return colors[4];                    // FFF9C4 - Light yellow
  }
}
